// Browser half of Ask Rize's support tickets (spec:
// docs/superpowers/specs/2026-10-10-ask-rize-support-tickets-design.md).
//
// The limits MIRROR supabase/functions/_shared/support-ticket.ts: the Vite bundle
// can't import Deno code, so tests/unit/ask-rize-support-ticket.test.mjs pins the
// two equal. PURE (no '@/' imports) so node --test can import it.

export const TICKET_TYPES = ['bug', 'feature_request'] as const
export type TicketType = (typeof TICKET_TYPES)[number]

export const TICKET_URGENCIES = ['low', 'medium', 'high'] as const
export type TicketUrgency = (typeof TICKET_URGENCIES)[number]

export const TITLE_MAX = 200
export const DETAILS_MAX = 5000

/** HELLA Projects' own caps. Screenshots and screen recordings (RIZ089). */
export const ATTACHMENT_MAX_COUNT = 3
export const ATTACHMENT_MAX_BYTES = 100 * 1024 * 1024
export const ATTACHMENT_TYPES_BY_EXT: Record<string, string> = {
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  webp: 'image/webp',
  mp4: 'video/mp4',
  m4v: 'video/mp4',
  mov: 'video/quicktime',
  webm: 'video/webm',
}
export const ATTACHMENT_TYPES: readonly string[] = [...new Set(Object.values(ATTACHMENT_TYPES_BY_EXT))]
export const ATTACHMENT_GENERIC_TYPES: readonly string[] = ['', 'application/octet-stream', 'binary/octet-stream']
/** HELLA Projects' signed upload URLs last two hours. */
export const SESSION_MAX_AGE_MS = 2 * 60 * 60 * 1000
/** For the file picker: the types plus their extensions (a .mov can arrive with no type). */
export const ATTACHMENT_ACCEPT = [...ATTACHMENT_TYPES, ...Object.keys(ATTACHMENT_TYPES_BY_EXT).map((ext) => `.${ext}`)].join(',')

export const TYPE_LABELS: Record<TicketType, string> = { bug: 'Problem', feature_request: 'Request' }
export const URGENCY_LABELS: Record<TicketUrgency, string> = { low: 'Low', medium: 'Normal', high: 'Urgent' }

export interface TicketDraft {
  type: TicketType
  urgency: TicketUrgency
  title: string
  description: string
}

export type TicketStatus = 'open' | 'sent' | 'dismissed'

export interface TicketState {
  draft: TicketDraft
  status: TicketStatus
}

function isOneOf<T extends string>(list: readonly T[], value: unknown): value is T {
  return typeof value === 'string' && (list as readonly string[]).includes(value)
}

/** A ticket_draft stream event's payload, or null when it isn't one. */
export function parseTicketDraft(value: unknown): TicketDraft | null {
  if (!value || typeof value !== 'object') return null
  const { type, urgency, title, description } = value as Record<string, unknown>
  if (!isOneOf(TICKET_TYPES, type) || !isOneOf(TICKET_URGENCIES, urgency)) return null
  if (typeof title !== 'string' || typeof description !== 'string') return null
  return { type, urgency, title: title.slice(0, TITLE_MAX), description: description.slice(0, DETAILS_MAX) }
}

interface WithTicket {
  id: string
  ticket?: TicketState
}

/** A new draft on message `id`. Any older draft still open is closed: one open card at a time. */
export function withTicketDraft<M extends WithTicket>(messages: M[], id: string, draft: TicketDraft): M[] {
  return messages.map((m) => {
    if (m.id === id) {
      const ticket: TicketState = { draft, status: 'open' }
      return { ...m, ticket }
    }
    if (m.ticket?.status === 'open') {
      const ticket: TicketState = { ...m.ticket, status: 'dismissed' }
      return { ...m, ticket }
    }
    return m
  })
}

/** Set one message's ticket status. A message that is gone, or has no ticket, is left alone. */
export function withTicketStatus<M extends WithTicket>(messages: M[], id: string, status: TicketStatus): M[] {
  return messages.map((m) => {
    if (m.id !== id || !m.ticket) return m
    const ticket: TicketState = { ...m.ticket, status }
    return { ...m, ticket }
  })
}

/** True while any draft card is still open (not sent, not dismissed). */
export function hasOpenTicket(messages: readonly WithTicket[]): boolean {
  return messages.some((m) => m.ticket?.status === 'open')
}

export function ticketSentMessage(reference: string): string {
  return `Sent to the Rize OS support team. Your reference is ${reference}. They’ll follow up if they need more.`
}

/** Mirror of the edge resolveAttachmentType: the declared type if we take it, else (when generic) the extension's. */
export function resolveAttachmentType(declared: unknown, name: unknown): string | null {
  const type = typeof declared === 'string' ? declared.trim().toLowerCase() : ''
  if (ATTACHMENT_TYPES.includes(type)) return type
  if (!ATTACHMENT_GENERIC_TYPES.includes(type) || typeof name !== 'string') return null
  const dot = name.lastIndexOf('.')
  if (dot < 0) return null
  return ATTACHMENT_TYPES_BY_EXT[name.slice(dot + 1).toLowerCase()] ?? null
}

/** Files picked in the card: the ones that fit on the ticket, plus why any were refused. */
export function checkAttachments(files: readonly File[], room: number): { accepted: File[]; refused: string[] } {
  const accepted: File[] = []
  const refused: string[] = []
  for (const file of files) {
    if (!resolveAttachmentType(file.type, file.name)) {
      refused.push(`${file.name}: attach a screenshot (PNG, JPEG, GIF, WebP) or a recording (MP4, MOV, WebM).`)
    } else if (file.size <= 0) {
      refused.push(`${file.name} is empty.`)
    } else if (file.size > ATTACHMENT_MAX_BYTES) {
      refused.push(`${file.name} is larger than 100 MB. Trim the recording and try again.`)
    } else if (accepted.length >= room) {
      refused.push(`${file.name}: only ${ATTACHMENT_MAX_COUNT} files fit on one ticket.`)
    } else {
      accepted.push(file)
    }
  }
  return { accepted, refused }
}

// ── The upload session's rules, pure so they are tested (the sender in supportTicket.ts can't
// load under node). They decide the only things that can lose a file or duplicate a ticket.

interface SessionView {
  files: readonly File[]
  preparedAt: number
  uploads: readonly { done: boolean }[]
}

/**
 * Reuse the session for this exact set of files while it still serves. A session whose files all
 * went up is kept at any age: it may already be a ticket, and HELLA Projects returns that ticket
 * for it (or says PT410), where starting over would file a second one.
 */
export function sessionUsable(session: SessionView | null, files: readonly File[], now: number): boolean {
  if (!session || session.files.length !== files.length || !session.files.every((f, i) => f === files[i])) return false
  return now - session.preparedAt < SESSION_MAX_AGE_MS || session.uploads.every((u) => u.done)
}

/** After a refused PUT: a refused URL won't work twice. 413/415 are the file's fault, a network error nobody's. */
export function resetAfterUploadFailure(status: number | null): boolean {
  return status !== null && status >= 400 && status < 500 && status !== 413 && status !== 415
}

/** After a refused ticket: 400 (an upload didn't finish) and 410 start over; an ambiguous failure keeps the session. */
export function resetAfterSendFailure(status: number | null): boolean {
  return status === 400 || status === 410
}

export function uploadFailureMessage(name: string, status: number): string {
  if (status === 413 || status === 415) return `Remove ${name} and send again — it’s too large or a type the support team can’t take.`
  return `Couldn’t upload ${name}. Your ticket is still here; press Send to try again.`
}

/** "312 KB", "23.4 MB". */
export function formatBytes(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}
